import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import SeoHead from '../components/SeoHead';
import { SketchButton, SketchPageShell, SketchNavBar } from '../components/sketchy';
import { getGuideBySlug, guidePath, MUSIC_GUIDES } from '../content/guides/musicGuides';
import { articleJsonLd } from '../seo/jsonLd';
import { absoluteUrl } from '../seo/seoConfig';
import ga from '../services/analytics';

export default function GuidePage() {
  const { slug = '' } = useParams();
  const guide = getGuideBySlug(slug);

  useEffect(() => {
    if (guide) {
      ga.event('guide_view', { guide_slug: guide.slug, page_path: guidePath(guide.slug) });
    }
  }, [guide]);

  if (!guide) {
    return (
      <SketchPageShell className="min-h-screen flex flex-col items-center justify-center px-6">
        <h1 className="text-2xl font-bold mb-4">Guide not found</h1>
        <Link to="/docs" className="text-[var(--brand-from)] hover:underline">
          Back to documentation
        </Link>
      </SketchPageShell>
    );
  }

  const path = guidePath(guide.slug);
  const seo = {
    title: `${guide.title} — Cassette`,
    description: guide.description,
    path,
    ogType: 'article' as const,
  };

  return (
    <SketchPageShell className="min-h-screen flex flex-col">
      <SeoHead
        meta={seo}
        jsonLd={articleJsonLd({ title: guide.title, description: guide.description, path })}
      />
      <SketchNavBar>
        <div className="max-w-3xl mx-auto w-full">
          <div className="flex h-16 items-center">
            <Link
              to="/docs"
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--color-hover)] transition-colors group"
            >
              <ArrowLeftIcon className="h-5 w-5 group-hover:-translate-x-1 transition-transform duration-200" />
              <span className="text-sm font-medium">Documentation</span>
            </Link>
          </div>
        </div>
      </SketchNavBar>

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <article className="max-w-3xl mx-auto prose prose-neutral dark:prose-invert">
          <h1 className="text-3xl font-bold mb-4 text-[var(--color-text)]">{guide.title}</h1>
          <p className="text-lg text-[var(--color-muted)] mb-8 leading-relaxed">{guide.intro}</p>

          {guide.sections.map((section) => (
            <section key={section.heading} className="mb-8">
              <h2 className="text-xl font-semibold mb-3 text-[var(--color-text)]">{section.heading}</h2>
              {section.paragraphs?.map((p) => (
                <p key={p.slice(0, 40)} className="text-[var(--color-text)] mb-3 leading-relaxed">
                  {p}
                </p>
              ))}
              {section.bullets && (
                <ul className="list-disc ml-6 space-y-2 text-[var(--color-text)]">
                  {section.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <div className="mt-10 pt-8 border-t border-[var(--color-border)]">
            <Link to={guide.slug === 'mcp-cursor-claude' ? '/docs/mcp' : '/auth/login'}>
              <SketchButton variant="primary">{guide.ctaLabel}</SketchButton>
            </Link>
          </div>

          <nav className="mt-12" aria-label="Related guides">
            <h2 className="text-lg font-semibold mb-4 text-[var(--color-text)]">More guides</h2>
            <ul className="space-y-2">
              {MUSIC_GUIDES.filter((g) => g.slug !== guide.slug).map((g) => (
                <li key={g.slug}>
                  <Link
                    to={guidePath(g.slug)}
                    className="text-[var(--brand-from)] hover:underline"
                    onClick={() => ga.event('guide_related_click', { from: guide.slug, to: g.slug })}
                  >
                    {g.title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </article>
      </main>
    </SketchPageShell>
  );
}

export function guideAbsoluteUrls(): string[] {
  return MUSIC_GUIDES.map((g) => absoluteUrl(guidePath(g.slug)));
}
